import useSWR from 'swr';
import { authenticatedFetcher } from '@/lib/swr';
import type { Notification, NotificationCount, NotificationFilter } from '@/types';
import { PP_BASE_URL } from '@/lib/env';

type ApiResponseWrapper<T> = {
  status?: number;
  data?: T;
  meta?: {
    total?: number;
    [key: string]: any;
  };
  notifications?: Notification[];
  total?: number;
};

const getPPBaseUrl = () => PP_BASE_URL;

/**
 * Hook to fetch and manage notifications directly from .NET backend API
 *
 * @param filter - Filter options (status, type, limit, offset)
 * @param config - SWR configuration options
 * @returns notifications, total count, loading state, and mutation functions
 */
export function useNotifications(filter: NotificationFilter = {}, config?: { refreshInterval?: number }) {
  const params = new URLSearchParams();

  if (filter.status) params.append('status', filter.status);
  if (filter.type) params.append('type', filter.type);
  if (filter.limit) params.append('limit', filter.limit.toString());
  if (filter.offset) params.append('offset', filter.offset.toString());

  const ppBaseUrl = getPPBaseUrl();
  const queryString = params.toString();
  const url = `${ppBaseUrl}/notifications${queryString ? `?${queryString}` : ''}`;

  const { data, error, isLoading, mutate } = useSWR<ApiResponseWrapper<Notification[]>>(url, authenticatedFetcher, {
    refreshInterval: config?.refreshInterval ?? 30000, // Default 30s
    revalidateOnFocus: false,
    dedupingInterval: 2000,
  });

  // Normalize notifications array from either .NET envelope ({ data: [...] }) or legacy format ({ notifications: [...] })
  const notifications: Notification[] = Array.isArray(data?.data)
    ? data!.data
    : Array.isArray(data?.notifications)
      ? data!.notifications
      : [];

  // Normalize total count from .NET metadata ({ meta: { total: N } }) or legacy object ({ total: N })
  const totalCount: number = data?.meta?.total ?? data?.total ?? notifications.length;

  /**
   * Mark a notification as read directly on .NET backend API
   */
  const markAsRead = async (id: number) => {
    try {
      // Optimistic update
      if (data) {
        const updatedList = notifications.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date() } : n));

        mutate(
          data.data ? { ...data, data: updatedList } : { ...data, notifications: updatedList },
          false // Don't revalidate immediately
        );
      }

      // Make direct API call to .NET backend
      await authenticatedFetcher(`${ppBaseUrl}/notifications/${id}/read`, {
        method: 'PATCH',
      });

      // Revalidate
      mutate();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert optimistic update on error
      mutate();
    }
  };

  /**
   * Mark all notifications as read directly on .NET backend API
   */
  const markAllAsRead = async () => {
    try {
      // Optimistic update
      if (data) {
        const updatedList = notifications.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date(),
        }));

        mutate(data.data ? { ...data, data: updatedList } : { ...data, notifications: updatedList }, false);
      }

      // Make direct API call to .NET backend
      await authenticatedFetcher(`${ppBaseUrl}/notifications/mark-all-read`, {
        method: 'POST',
      });

      // Revalidate
      mutate();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      // Revert on error
      mutate();
    }
  };

  /**
   * Manually refresh notifications
   */
  const refresh = () => mutate();

  return {
    notifications,
    total: totalCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    refresh,
  };
}

/**
 * Hook to fetch notification count directly from .NET backend API
 *
 * @param config - SWR configuration options
 * @returns unread count, total count, and refresh function
 */
export function useNotificationCount(config?: { refreshInterval?: number; showToast?: boolean; locale?: string }) {
  const ppBaseUrl = getPPBaseUrl();
  const url = `${ppBaseUrl}/notifications/count`;

  const { data, error, isLoading, mutate } = useSWR<ApiResponseWrapper<NotificationCount> | NotificationCount>(
    url,
    authenticatedFetcher,
    {
      refreshInterval: config?.refreshInterval ?? 30000, // Default 30s
      revalidateOnFocus: false,
      dedupingInterval: 2000,
    }
  );

  // Normalize count object from either .NET envelope ({ data: { total, unread } }) or direct object ({ total, unread })
  const countData: NotificationCount | undefined =
    data && 'data' in data && data.data && typeof data.data === 'object'
      ? (data.data as NotificationCount)
      : (data as NotificationCount);

  return {
    count: countData,
    unread: countData?.unread ?? 0,
    total: countData?.total ?? 0,
    isLoading,
    error,
    refresh: () => mutate(),
  };
}
